/**
 * チバリヨ - S6 +新規モーダル (ボトムシート)
 *
 * 使い方:
 *   openS6Modal({
 *     onSelectReport: () => { ... },
 *     onSelectTask: () => { ... },
 *   });
 *
 * 仕様:
 *   - ボトムシート (下からスライドアップ)
 *   - 選択肢: 日報を作る / タスクを作る
 *   - 閉じ方: 背景タップ / × / キャンセル / 下スワイプ / Esc
 *   - role="dialog" + aria-modal="true"
 *   - 開いた時、最初の選択肢にフォーカス
 *   - 200ms スライドアップ (prefers-reduced-motion はフェードのみ)
 */

let activeOverlay = null;
let prevFocusEl = null;

const ICON_REPORT =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="14 3 14 9 20 9"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>';

const ICON_TASK =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M8 11l3 3 5-6"/></svg>';

export function openS6Modal(opts) {
  // 多重起動防止
  if (activeOverlay) return;

  const { onSelectReport, onSelectTask } = opts || {};

  prevFocusEl = document.activeElement;

  const overlay = document.createElement('div');
  overlay.className = 's6-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', '新規作成');
  overlay.innerHTML = `
    <div class="s6-sheet">
      <div class="s6-handle-wrap" data-handle>
        <div class="s6-handle"></div>
      </div>
      <div class="s6-header">
        <h2 class="s6-title">新規作成</h2>
        <button type="button" class="s6-close" aria-label="閉じる">×</button>
      </div>
      <div class="s6-options">
        <button type="button" class="s6-option" data-action="report">
          <div class="s6-option-icon is-report">${ICON_REPORT}</div>
          <div class="s6-option-text">
            <div class="s6-option-title">日報を作る</div>
            <div class="s6-option-desc">今日の活動を記録します</div>
          </div>
        </button>
        <button type="button" class="s6-option" data-action="task">
          <div class="s6-option-icon is-task">${ICON_TASK}</div>
          <div class="s6-option-text">
            <div class="s6-option-title">タスクを作る</div>
            <div class="s6-option-desc">やることリストに追加します</div>
          </div>
        </button>
      </div>
      <button type="button" class="s6-cancel" data-action="cancel">キャンセル</button>
    </div>
  `;
  document.body.appendChild(overlay);
  activeOverlay = overlay;

  // スライドアップアニメーション
  requestAnimationFrame(() => {
    overlay.classList.add('is-open');
  });

  // フォーカス
  const firstOption = overlay.querySelector('.s6-option');
  if (firstOption) {
    setTimeout(() => firstOption.focus(), 50);
  }

  const sheet = overlay.querySelector('.s6-sheet');

  // ----- 閉じる処理 -----
  function close() {
    overlay.classList.remove('is-open');
    document.removeEventListener('keydown', onKeyDown);
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      if (activeOverlay === overlay) activeOverlay = null;
      if (prevFocusEl && typeof prevFocusEl.focus === 'function') {
        try { prevFocusEl.focus(); } catch (e) { /* noop */ }
      }
    }, 200);
  }

  // ----- 選択ハンドラ -----
  overlay.addEventListener('click', (e) => {
    // 背景タップ
    if (e.target === overlay) {
      close();
      return;
    }
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    const action = btn.dataset.action;
    if (action === 'cancel') {
      close();
    } else if (action === 'report') {
      close();
      setTimeout(() => onSelectReport && onSelectReport(), 220);
    } else if (action === 'task') {
      close();
      setTimeout(() => onSelectTask && onSelectTask(), 220);
    }
  });

  overlay.querySelector('.s6-close').addEventListener('click', close);

  // ----- Esc -----
  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }
  document.addEventListener('keydown', onKeyDown);

  // ----- 下スワイプで閉じる -----
  let dragStartY = null;
  let dragOffset = 0;
  const handleEl = overlay.querySelector('[data-handle]');

  function onTouchStart(e) {
    if (e.touches.length !== 1) return;
    dragStartY = e.touches[0].clientY;
    dragOffset = 0;
    sheet.classList.add('is-dragging');
  }
  function onTouchMove(e) {
    if (dragStartY == null) return;
    dragOffset = e.touches[0].clientY - dragStartY;
    if (dragOffset < 0) dragOffset = 0;
    sheet.style.transform = `translateY(${dragOffset}px)`;
  }
  function onTouchEnd() {
    if (dragStartY == null) return;
    sheet.classList.remove('is-dragging');
    if (dragOffset > 80) {
      // 閉じる
      sheet.style.transform = '';
      close();
    } else {
      // 元に戻す
      sheet.style.transform = '';
    }
    dragStartY = null;
    dragOffset = 0;
  }
  handleEl.addEventListener('touchstart', onTouchStart, { passive: true });
  handleEl.addEventListener('touchmove', onTouchMove, { passive: true });
  handleEl.addEventListener('touchend', onTouchEnd);
}
